import { generateRandomHex, request } from '@nextgen-web-framework/core';
import { BankDetails } from '../models/BankDetails';
import { BankAccountDetails } from '../models/BankAccountDetails';
import { BankAccountUsage } from '../models/BankAccountUsage';
import type { CustomRequestOptions } from '../../types';
import { BankDetailFilters } from '../models/BankDetailFilters';
import { BankDetailsPaginatedResponse } from '../models/BankDetailsPaginatedResponse';
import { AccountUserDetails } from '../models/AccountUserDetails';

const appServiceUrl = '/compaymentas/v1';

const basePath = (baseURL: string) => baseURL + appServiceUrl;

export const BankAccountApi = {
  get basePath() {
    const baseUrl = ApiConfigService.getBaseUrl();
    return `${baseUrl}${appServiceUrl}`;
  },

  async getBanksList(options?: CustomRequestOptions): Promise<BankDetails[]> {
    return await request.get({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}` + '/bank-details',
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getAllBanksList(
    payload: BankDetailFilters,
    options?: CustomRequestOptions,
  ): Promise<BankDetailsPaginatedResponse> {
    return await request.post({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}` + '/bank-details',
      body: payload as unknown as BodyInit,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getAccountBasedBanks(accountPurpose: string, options?: CustomRequestOptions): Promise<BankAccountDetails[]> {
    return await request.get({
      url: `${
        options?.baseURL ? basePath(options.baseURL) : this.basePath
      }/bank-accounts?accountUsage=${accountPurpose}`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getBanksAccounts(
    accountId: string,
    accountPurpose: string,
    options?: CustomRequestOptions,
  ): Promise<BankAccountDetails[]> {
    return await request.get({
      url: `${
        options?.baseURL ? basePath(options.baseURL) : this.basePath
      }/bank-accounts?accountId=${accountId}&accountUsage=${accountPurpose}`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async addBankAccount(formData: FormData, options?: CustomRequestOptions) {
    const uuid = `${generateRandomHex(9)}-${generateRandomHex(4)}-${generateRandomHex(4)}-${generateRandomHex(
      4,
    )}-${generateRandomHex(13)}`;
    return await request.post(
      {
        url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/bank-accounts`,
        body: formData as unknown as BodyInit,
        headers: {
          'X-Idempotency-Key': uuid,
          ...options?.headers,
        },
      },
      {
        skipIdempotency: true,
        skipDefaultContentType: true,
      },
    );
  },

  async getAddedBankList(
    accountUsage = '' as BankAccountUsage,
    options?: CustomRequestOptions,
  ): Promise<BankAccountDetails[]> {
    return await request.get({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/bank-accounts?accountUsage=${accountUsage}`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getBankDetailsById(
    bankId: string,
    accountUsage: BankAccountUsage,
    options?: CustomRequestOptions,
  ): Promise<BankAccountDetails> {
    return await request.get({
      url: `${
        options?.baseURL ? basePath(options.baseURL) : this.basePath
      }/bank-accounts/${bankId}?accountUsage=${accountUsage}`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async updateBankAccount(payload: FormData, id: string, options?: CustomRequestOptions) {
    const uuid = `${generateRandomHex(9)}-${generateRandomHex(4)}-${generateRandomHex(4)}-${generateRandomHex(
      4,
    )}-${generateRandomHex(13)}`;
    return await request.put(
      {
        url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/bank-accounts/${id}`,
        body: payload as unknown as BodyInit,
        headers: {
          'X-Idempotency-Key': uuid,
          ...options?.headers,
        },
      },
      {
        skipIdempotency: true,
        skipDefaultContentType: true,
      },
    );
  },
  async getuserAccountDetails(
    accountUsage: BankAccountUsage,
    options?: CustomRequestOptions,
  ): Promise<AccountUserDetails[]> {
    return await request.get({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/user-accounts/details?accountUsage=${accountUsage}`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },
};
